import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { OnboardingScreenEditRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useAddOnboardingScreen = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: api.postOnboardingScreensV1,
    onSuccess() {
      showFlashToast({ title: t("page.onboarding-screens.new.notification.success"), type: "success" });
    },
    onError() {
      showFlashToast({ title: t("page.onboarding-screens.new.notification.failed"), type: "error" });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ONBOARDING_SCREENS_LIST(projectId) });
    },
  });
};

const useUpdateOnboardingScreen = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: { onboardingScreenId: string; data: OnboardingScreenEditRequest }) =>
      api.putOnboardingScreensV1(payload.onboardingScreenId, payload.data),
    onSuccess() {
      showFlashToast({ title: t("page.onboarding-screens.edit.notification.success"), type: "success" });
    },
    onError() {
      showFlashToast({ title: t("page.onboarding-screens.edit.notification.failed"), type: "error" });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ONBOARDING_SCREENS_LIST(projectId) });
    },
  });
};

const useDeleteOnboardingScreen = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: { onboardingScreenId: string }) =>
      api.deleteOnboardingScreensByIdV1(payload.onboardingScreenId),
    onSuccess() {
      showFlashToast({
        type: "success",
        title: t("page.onboarding-screens.actions.delete.notifications.success"),
      });
    },
    onError() {
      showFlashToast({
        type: "error",
        title: t("page.onboarding-screens.actions.delete.notifications.failed"),
      });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ONBOARDING_SCREENS_LIST(projectId) });
    },
  });
};

export const onboardingScreenMutations = {
  useUpdateOnboardingScreen,
  useAddOnboardingScreen,
  useDeleteOnboardingScreen,
};
